<script setup lang="ts">
/**
 * MessageToast
 *
 * Kurze, selbst verschwindende Popup-Benachrichtigung bei neuer Nachricht -
 * bewusst statt eines schwebenden Dauer-Chat-Widgets (Nutzer-Entscheidung:
 * "alles andere ist zu viel Spielerei"). Nutzt message.store.ts's bereits
 * laufendes 45s-Polling (kein WebSocket/SSE im Projekt vorhanden) - erkennt
 * dort einen Anstieg der Ungelesen-Zahl und setzt `latestNotification`.
 * Global in AdminLayout.vue eingebunden, damit sie auf jeder Seite erscheint.
 */
import { watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useMessageStore } from '@/modules/messages/stores/message.store'

const router = useRouter()
const messageStore = useMessageStore()
const visible = ref(false)
let hideHandle: ReturnType<typeof setTimeout> | null = null

watch(
  () => messageStore.latestNotification,
  (notification) => {
    if (!notification) return
    visible.value = true
    if (hideHandle) clearTimeout(hideHandle)
    hideHandle = setTimeout(() => {
      visible.value = false
      messageStore.clearNotification()
    }, 6000)
  }
)

function openMessages() {
  visible.value = false
  messageStore.clearNotification()
  router.push('/messages')
}

function dismiss() {
  visible.value = false
  messageStore.clearNotification()
}
</script>

<template>
  <Transition name="toast">
    <button v-if="visible && messageStore.latestNotification" class="message-toast" @click="openMessages">
      <div class="toast-icon">💬</div>
      <div class="toast-body">
        <div class="toast-title">{{ messageStore.latestNotification.title }}</div>
        <div class="toast-preview">{{ messageStore.latestNotification.preview }}</div>
      </div>
      <button class="toast-close" title="Schließen" @click.stop="dismiss">×</button>
    </button>
  </Transition>
</template>

<style scoped>
.message-toast {
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 200;
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  background: #1a1a1a;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
  max-width: 320px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  text-align: left;
}
.toast-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}
.toast-body {
  flex: 1;
  min-width: 0;
}
.toast-title {
  font-weight: 600;
  font-size: 0.85rem;
}
.toast-preview {
  font-size: 0.78rem;
  opacity: 0.8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 0.1rem;
}
.toast-close {
  border: none;
  background: none;
  color: #fff;
  opacity: 0.7;
  font-size: 1rem;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
}
.toast-close:hover {
  opacity: 1;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
